import { DoneActorEvent, sendTo } from 'xstate';
import { FacilityFormValues, XStateAxiosError } from '../../types';
import { sendAxiosErrorAlert } from '../../utils';
import { emptyFacilityForm } from './constants';
import { OpenFacilityEvent, SubmitFacilityEvent } from './events';
import { Scope } from './types';

export const setParentId = {
  parentId: ({ self }: Scope) => {
    return self._parent?.id || '';
  },
};

export const openModal = {
  modal: true,
};

export const closeModal = {
  modal: false,
};

export const setLoading = {
  loading: true,
};

export const resetLoading = {
  loading: false,
};

/*
 * [MCV-1536] Address the parent directly instead of `system.get(context.parentId)`.
 *
 * `setParentId` stores `self._parent?.id`, but `system.get()` resolves by *systemId*,
 * which is only registered when an actor is spawned with an explicit `systemId:`.
 * The listing wizard's facilities step is spawned with `id` only — the one sibling in
 * jobListing.machine.ts (:101) that omits it, unlike :107/:112/:117/:122/:127 — so the
 * lookup returned undefined. xstate 5's resolveSendTo falls back to `actorScope.self`
 * on an undefined target, so these events were silently re-delivered to the sender and
 * the parent never heard about a newly created facility.
 *
 * `self._parent` is what these actions always meant (parentId was derived from it) and
 * works for every consumer regardless of systemId registration. Not `sendParent`: eight
 * specs create facilityMachine as a parentless root, where that throws — `?? self`
 * preserves today's no-op there.
 *
 * `setParentId`/`context.parentId` are left in place deliberately; removing them touches
 * types + a spec and belongs in a dead-code pass, not a bug fix.
 */
export const notifyParentOnOpen = sendTo(
  ({ self }: Scope) => self._parent ?? self,
  () => ({ type: 'ON_OPEN_FACILITY_FORM' })
);

export const notifyParentOnClose = sendTo(
  ({ self }: Scope) => self._parent ?? self,
  () => ({ type: 'ON_CLOSE_FACILITY_FORM' })
);

export const updateFormOnSuccess = {
  form: ({ event, context }: Scope) => {
    const { output } = event as unknown as DoneActorEvent<FacilityFormValues>;
    return { ...context.form, ...output };
  },
  modal: false,
  loading: false,
};

export const notifyParentOnCreation = sendTo(
  ({ self }: Scope) => self._parent ?? self,
  ({ context }: Scope) => ({
    type: 'ON_FACILITY_CREATED',
    facility: context.form,
  })
);

export const sendError = sendTo(
  ({ system }: Scope) => system.get('userState'),
  ({ event }: Scope) => sendAxiosErrorAlert(event as XStateAxiosError)
);

export const resetOnError = {
  modal: false,
  loading: false,
};

export const resetForm = {
  form: emptyFacilityForm,
};

/*
 * [MCV-1638] Carry the name typed into the facility picker into the "Add New
 * Facility" dialog.
 *
 * Training and Employment used to reach this dialog through a standalone
 * "Add a Facility" button, so the form always opened blank and retyping the
 * name was expected. Now that the add affordance is the in-menu `Add "<text>"`
 * row (matching Education), opening blank would silently discard what the user
 * just typed.
 *
 * Runs on the ON_OPEN transition rather than as a $_OPENED entry action:
 * $_OPENED is also re-entered by ON_BACK_TO_FORM from the duplicates step,
 * where the event carries no prefillName and the in-progress form must survive.
 * No prefillName leaves context.form exactly as it was, which is what the
 * standalone button (still used by market/facility/EmployerFacilityForm) needs.
 */
export const seedFormName = {
  form: ({ context, event }: Scope) => {
    const { prefillName } = event as unknown as OpenFacilityEvent;
    if (!prefillName?.trim()) return context.form;
    return { ...context.form, label: prefillName.trim(), userAdded: true };
  },
};

export const setDuplicatesFound = {
  duplicates: ({ event }: Scope) => {
    const { output } = event as unknown as DoneActorEvent<FacilityFormValues[]>;
    return output;
  },
  loading: false,
};

export const clearDuplicates = {
  duplicates: [] as FacilityFormValues[],
  pendingValues: emptyFacilityForm,
  showDuplicates: false,
};

export const setPendingValues = {
  pendingValues: ({ event }: Scope) => {
    const submitEvent = event as unknown as SubmitFacilityEvent;
    return submitEvent.values;
  },
};

export const selectExistingFacility = {
  form: ({ event }: Scope) => {
    const selectEvent = event as unknown as { type: string; facility: FacilityFormValues };
    return selectEvent.facility;
  },
  modal: false,
  loading: false,
  duplicates: [] as FacilityFormValues[],
  pendingValues: emptyFacilityForm,
  showDuplicates: false,
};

export const showDuplicatesModal = {
  showDuplicates: true,
};

// Reuses the same event shape as notifyParentOnCreation — parent handles both
// creation and selection identically via ON_FACILITY_CREATED
export const notifyParentOnSelection = notifyParentOnCreation;
